/**
 * Mapper App — Zustand store for map UI state.
 * Manages: selected feature, draw mode, panel visibility, import state.
 */

import { create } from 'zustand';
import type {
  ArmedInfrastructureTemplate,
  MapFeature,
  MapInfrastructure,
  MapLayer,
  MapParcel,
  MapRasterOverlay,
  DrawMode,
  ImportPreview,
  InfraCategoryType,
  MapperSharedParcelGroup,
} from './types';
import type { Map as MapLibreMap } from 'maplibre-gl';

function dedupeSelectedParcels(parcels: MapParcel[]): MapParcel[] {
  const seen = new Set<string>();
  const unique: MapParcel[] = [];
  for (const parcel of parcels) {
    if (!parcel?.id || seen.has(parcel.id)) continue;
    seen.add(parcel.id);
    unique.push(parcel);
  }
  return unique;
}

interface MapState {
  // Map instance ref
  map: MapLibreMap | null;
  setMap: (map: MapLibreMap | null) => void;

  // Selection
  selectedFeature: MapFeature | null;
  selectedLayer: MapLayer | null;
  selectedInfrastructure: MapInfrastructure | null;
  setSelectedFeature: (feature: MapFeature | null) => void;
  setSelectedLayer: (layer: MapLayer | null) => void;
  setSelectedInfrastructure: (infra: MapInfrastructure | null) => void;

  // Active parcel context (property boundary that scopes all operations)
  activeParcel: MapParcel | null;
  selectedParcels: MapParcel[];
  activeFarmGroup: MapperSharedParcelGroup | null;
  setActiveParcel: (parcel: MapParcel | null) => void;
  setActiveFarmGroup: (group: MapperSharedParcelGroup | null) => void;
  toggleParcelSelection: (parcel: MapParcel) => void;
  clearParcelSelection: () => void;
  guidedParcelSelectionActive: boolean;
  guidedParcelSelectionParcel: MapParcel | null;
  setGuidedParcelSelectionActive: (active: boolean) => void;
  setGuidedParcelSelectionParcel: (parcel: MapParcel | null) => void;
  parcelViewportBbox: string | undefined;
  parcelViewportZoom: number;
  setParcelViewport: (bbox: string | undefined, zoom: number) => void;

  // Active category (function-based, filters infrastructure type options)
  activeCategory: InfraCategoryType | null;
  setActiveCategory: (category: InfraCategoryType | null) => void;

  // Boundary visibility
  showBoundaries: boolean;
  setShowBoundaries: (show: boolean) => void;
  parcelLayerScope: 'viewport' | 'farm_only';
  setParcelLayerScope: (scope: 'viewport' | 'farm_only') => void;
  assetLayerMode: 'all' | 'asset_only';
  setAssetLayerMode: (mode: 'all' | 'asset_only') => void;

  // GEE / imagery overlay
  geeOverlay: MapRasterOverlay | null;
  setGeeOverlay: (overlay: MapRasterOverlay | null) => void;
  updateGeeOverlay: (patch: Partial<MapRasterOverlay>) => void;
  clearGeeOverlay: () => void;

  // Panels
  layerPanelOpen: boolean;
  inspectorOpen: boolean;
  devicePaletteOpen: boolean;
  parcelInspectorOpen: boolean;
  toggleLayerPanel: () => void;
  setInspectorOpen: (open: boolean) => void;
  setDevicePaletteOpen: (open: boolean) => void;
  setParcelInspectorOpen: (open: boolean) => void;

  // Drawing
  drawMode: DrawMode;
  activeDrawLayerId: string | null;
  setDrawMode: (mode: DrawMode, layerId?: string | null) => void;
  parcelCreationMode: boolean;
  setParcelCreationMode: (active: boolean) => void;
  pendingParcelGeometry: GeoJSON.Geometry | null;
  setPendingParcelGeometry: (geom: GeoJSON.Geometry | null) => void;

  // Infrastructure classification (shown after draw completes)
  pendingDrawGeometry: GeoJSON.Geometry | null;
  setPendingDrawGeometry: (geom: GeoJSON.Geometry | null) => void;
  armedTemplate: ArmedInfrastructureTemplate | null;
  setArmedTemplate: (template: ArmedInfrastructureTemplate | null) => void;

  // Existing infrastructure geometry editing (visual on-map edit)
  editingInfrastructureGeometryId: string | null;
  editingInfrastructureGeometrySeed: GeoJSON.Geometry | null;
  editingInfrastructureGeometryDraft: GeoJSON.Geometry | null;
  startEditingInfrastructureGeometry: (infraId: string, geometry: GeoJSON.Geometry) => void;
  setEditingInfrastructureGeometryDraft: (geom: GeoJSON.Geometry | null) => void;
  clearEditingInfrastructureGeometry: () => void;

  // Import
  importPreview: ImportPreview | null;
  importStep: 'idle' | 'uploading' | 'parsing' | 'preview' | 'committing';
  setImportPreview: (preview: ImportPreview | null) => void;
  setImportStep: (step: 'idle' | 'uploading' | 'parsing' | 'preview' | 'committing') => void;
  clearImport: () => void;

  // Agentic
  agentRunning: boolean;
  agentEntityId: string | null;
  setAgentRunning: (running: boolean, entityId?: string | null) => void;

  // Context menu
  contextMenu: { x: number; y: number; lngLat: [number, number] } | null;
  setContextMenu: (menu: { x: number; y: number; lngLat: [number, number] } | null) => void;

  // Fly-to trigger
  flyToTarget: { center: [number, number]; zoom?: number; bounds?: [number, number, number, number] } | null;
  triggerFlyTo: (target: { center: [number, number]; zoom?: number; bounds?: [number, number, number, number] }) => void;
  clearFlyTo: () => void;

  // L4 live object positions (polled from telemetry-read)
  livePositions: Array<{ deviceId: string; lat?: number; lng?: number }>;
  setLivePositions: (positions: Array<{ deviceId: string; lat?: number; lng?: number }>) => void;
}

export const useMapStore = create<MapState>((set) => ({
  map: null,
  setMap: (map) => set({ map }),

  selectedFeature: null,
  selectedLayer: null,
  selectedInfrastructure: null,
  setSelectedFeature: (feature) => set({
    selectedFeature: feature,
    selectedInfrastructure: null,
    inspectorOpen: !!feature,
    parcelInspectorOpen: false,
    editingInfrastructureGeometryId: null,
    editingInfrastructureGeometrySeed: null,
    editingInfrastructureGeometryDraft: null,
  }),
  setSelectedLayer: (layer) => set({ selectedLayer: layer }),
  setSelectedInfrastructure: (infra) => set((state) => {
    const preserveGeometryEdit = !!infra && state.editingInfrastructureGeometryId === infra.id;
    return {
      selectedInfrastructure: infra,
      selectedFeature: null,
      inspectorOpen: !!infra,
      parcelInspectorOpen: false,
      editingInfrastructureGeometryId: preserveGeometryEdit ? state.editingInfrastructureGeometryId : null,
      editingInfrastructureGeometrySeed: preserveGeometryEdit ? state.editingInfrastructureGeometrySeed : null,
      editingInfrastructureGeometryDraft: preserveGeometryEdit ? state.editingInfrastructureGeometryDraft : null,
    };
  }),

  activeParcel: null,
  selectedParcels: [],
  activeFarmGroup: null,
  setActiveParcel: (parcel) => set((state) => {
    if (!parcel) {
      return {
        activeParcel: null,
        selectedParcels: [],
      };
    }
    const alreadySelected = state.selectedParcels.some((item) => item.id === parcel.id);
    if (!alreadySelected) {
      return {
        activeParcel: parcel,
        selectedParcels: [parcel],
      };
    }
    return {
      activeParcel: parcel,
      selectedParcels: state.selectedParcels.map((item) => (item.id === parcel.id ? parcel : item)),
    };
  }),
  setActiveFarmGroup: (group) => set({ activeFarmGroup: group }),
  toggleParcelSelection: (parcel) => set((state) => {
    const currentlySelected = state.selectedParcels.some((item) => item.id === parcel.id);
    if (currentlySelected) {
      const remaining = state.selectedParcels.filter((item) => item.id !== parcel.id);
      const nextActive = state.activeParcel?.id === parcel.id
        ? (remaining[remaining.length - 1] ?? null)
        : state.activeParcel;
      return {
        activeParcel: nextActive,
        selectedParcels: remaining,
        parcelInspectorOpen: remaining.length > 0 ? state.parcelInspectorOpen : false,
      };
    }
    return {
      activeParcel: parcel,
      selectedParcels: dedupeSelectedParcels([...state.selectedParcels, parcel]),
      parcelInspectorOpen: true,
    };
  }),
  clearParcelSelection: () => set({
    activeParcel: null,
    selectedParcels: [],
    parcelInspectorOpen: false,
  }),
  guidedParcelSelectionActive: false,
  guidedParcelSelectionParcel: null,
  setGuidedParcelSelectionActive: (active) => set({
    guidedParcelSelectionActive: active,
    guidedParcelSelectionParcel: null,
  }),
  setGuidedParcelSelectionParcel: (parcel) => set({ guidedParcelSelectionParcel: parcel }),
  parcelViewportBbox: undefined,
  parcelViewportZoom: 5,
  setParcelViewport: (bbox, zoom) => set((state) => {
    const normalizedZoom = Number.isFinite(zoom) ? Number(zoom.toFixed(1)) : state.parcelViewportZoom;
    return state.parcelViewportBbox === bbox && state.parcelViewportZoom === normalizedZoom
      ? state
      : {
        parcelViewportBbox: bbox,
        parcelViewportZoom: normalizedZoom,
      }
  }),

  activeCategory: null,
  setActiveCategory: (category) => set({ activeCategory: category }),

  showBoundaries: true,
  setShowBoundaries: (show) => set((state) => (
    state.showBoundaries === show ? state : { showBoundaries: show }
  )),
  parcelLayerScope: 'viewport',
  setParcelLayerScope: (scope) => set((state) => (
    state.parcelLayerScope === scope ? state : { parcelLayerScope: scope }
  )),
  assetLayerMode: 'all',
  setAssetLayerMode: (mode) => set({ assetLayerMode: mode }),

  geeOverlay: null,
  setGeeOverlay: (overlay) => set({ geeOverlay: overlay }),
  updateGeeOverlay: (patch) => set((state) => (
    state.geeOverlay
      ? { geeOverlay: { ...state.geeOverlay, ...patch } }
      : {}
  )),
  clearGeeOverlay: () => set({ geeOverlay: null }),

  layerPanelOpen: true,
  inspectorOpen: false,
  devicePaletteOpen: false,
  parcelInspectorOpen: false,
  toggleLayerPanel: () => set((s) => ({ layerPanelOpen: !s.layerPanelOpen })),
  setInspectorOpen: (open) => set((state) => ({
    inspectorOpen: open,
    editingInfrastructureGeometryId: open ? state.editingInfrastructureGeometryId : null,
    editingInfrastructureGeometrySeed: open ? state.editingInfrastructureGeometrySeed : null,
    editingInfrastructureGeometryDraft: open ? state.editingInfrastructureGeometryDraft : null,
  })),
  setDevicePaletteOpen: (open) => set({ devicePaletteOpen: open }),
  setParcelInspectorOpen: (open) => set({
    parcelInspectorOpen: open,
    editingInfrastructureGeometryId: null,
    editingInfrastructureGeometrySeed: null,
    editingInfrastructureGeometryDraft: null,
  }),

  drawMode: 'none',
  activeDrawLayerId: null,
  setDrawMode: (mode, layerId) => set({ drawMode: mode, activeDrawLayerId: layerId ?? null }),
  parcelCreationMode: false,
  setParcelCreationMode: (active) => set({ parcelCreationMode: active, pendingParcelGeometry: null }),
  pendingParcelGeometry: null,
  setPendingParcelGeometry: (geom) => set({ pendingParcelGeometry: geom }),

  pendingDrawGeometry: null,
  setPendingDrawGeometry: (geom) => set({ pendingDrawGeometry: geom }),
  armedTemplate: null,
  setArmedTemplate: (template) => set({ armedTemplate: template }),

  editingInfrastructureGeometryId: null,
  editingInfrastructureGeometrySeed: null,
  editingInfrastructureGeometryDraft: null,
  startEditingInfrastructureGeometry: (infraId, geometry) => set({
    editingInfrastructureGeometryId: infraId,
    editingInfrastructureGeometrySeed: geometry,
    editingInfrastructureGeometryDraft: geometry,
    pendingDrawGeometry: null,
    pendingParcelGeometry: null,
    parcelCreationMode: false,
    drawMode: 'none',
  }),
  setEditingInfrastructureGeometryDraft: (geom) => set({ editingInfrastructureGeometryDraft: geom }),
  clearEditingInfrastructureGeometry: () => set({
    editingInfrastructureGeometryId: null,
    editingInfrastructureGeometrySeed: null,
    editingInfrastructureGeometryDraft: null,
    pendingParcelGeometry: null,
    parcelCreationMode: false,
    drawMode: 'none',
  }),

  importPreview: null,
  importStep: 'idle',
  setImportPreview: (preview) => set({ importPreview: preview }),
  setImportStep: (step) => set({ importStep: step }),
  clearImport: () => set({ importPreview: null, importStep: 'idle' }),

  agentRunning: false,
  agentEntityId: null,
  setAgentRunning: (running, entityId) => set({ agentRunning: running, agentEntityId: entityId ?? null }),

  contextMenu: null,
  setContextMenu: (menu) => set({ contextMenu: menu }),

  flyToTarget: null,
  triggerFlyTo: (target) => set({ flyToTarget: target }),
  clearFlyTo: () => set({ flyToTarget: null }),

  livePositions: [],
  setLivePositions: (positions) => set({ livePositions: positions }),
}));
